import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ClipboardList } from 'lucide-react';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import QueryErrorBanner from '../../components/Common/QueryErrorBanner';
import SlaBadge from '../../components/Common/SlaBadge';
import { getAppHomePath } from '../../constants/navigation';
import { meService } from '../../services/api';

const KIND_LABELS = {
  tramitacao: 'Tramitação',
  denuncia: 'Denúncia',
  vistoria: 'Vistoria',
  condicionante: 'Condicionante',
};

function unwrapTasks(payload) {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

const MinhasTarefas = () => {
  const [kind, setKind] = useState('');
  const { data, isLoading, error } = useQuery(['me-tasks'], () => meService.getTasks().then((r) => r.data), {
    refetchInterval: 120_000,
  });
  const tasks = unwrapTasks(data);
  const summary = data?.summary || {};
  const filtered = useMemo(
    () => (kind ? tasks.filter((t) => t.kind === kind) : tasks),
    [tasks, kind]
  );

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title="Minhas tarefas"
          description="Pendências atribuídas a você, ordenadas pela urgência do prazo."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Minhas tarefas' },
          ]}
        />
        {error ? <QueryErrorBanner error={error} /> : null}
        {!data?.servidor_id && !isLoading ? (
          <p className="text-sm text-amber-800">
            Nenhum cadastro de servidor está vinculado à sua pessoa. A fila pessoal fica vazia até esse vínculo.
          </p>
        ) : data?.setor_name ? (
          <p className="text-sm text-gray-600">
            Setor: <span className="font-medium text-gray-900">{data.setor_name}</span>
          </p>
        ) : null}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            ['vencido', 'Vencidas', summary.vencido],
            ['atencao', 'Atenção', summary.atencao],
            ['no_prazo', 'No prazo', summary.no_prazo],
            ['sem_prazo', 'Sem prazo', summary.sem_prazo],
          ].map(([key, label, count]) => (
            <div key={key} className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase text-gray-500">{label}</p>
              <p className="text-2xl font-semibold">{count ?? 0}</p>
            </div>
          ))}
        </div>
        <div className="form-group max-w-xs">
          <label htmlFor="me-kind" className="form-label">
            Tipo
          </label>
          <select
            id="me-kind"
            className="form-select"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            <option value="">Todos</option>
            {Object.entries(KIND_LABELS).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="data-list-card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Tarefa</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">SLA</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase text-gray-500"> </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                    Carregando...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                    <ClipboardList className="mx-auto mb-2 h-6 w-6" />
                    Nenhuma pendência atribuída a você.
                  </td>
                </tr>
              ) : (
                filtered.map((item) => (
                  <tr key={`${item.kind}-${item.id}`}>
                    <td className="px-6 py-4 text-sm text-gray-600">{KIND_LABELS[item.kind] || item.kind}</td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">{item.titulo}</p>
                      <p className="text-xs text-gray-500">{item.subtitulo}</p>
                    </td>
                    <td className="px-6 py-4">
                      <SlaBadge sla={item.sla} diasRestantes={item.dias_restantes} prazo={item.prazo} />
                    </td>
                    <td className="px-6 py-4 text-right text-sm">
                      {item.link_href ? (
                        <Link to={item.link_href} className="text-primary-600 hover:text-primary-800">
                          Abrir
                        </Link>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </FormPageShell>
  );
};

export default MinhasTarefas;
export { KIND_LABELS, unwrapTasks };
